import React, { useCallback, useState } from "react";
import { List, ListItemButton, ListItemText, ListItemIcon } from "@mui/material";
import { Search } from "@vibe/core";
import Spinner from "../Spinner";
import { useList, useSearchList } from "../../hooks/ui";
import type { GroupsListProps as GroupsListPropsType } from "./addItem.types";
import { cn } from "../../utils/tailwind-merge";
import AddItemSubtitle from "./AddItemSubtitle";
import { useAddItemToGroup } from "./useAddItemToGroup";

const LIST_ITEM_TYPOGRAPHY = {
  fontSize: "0.875rem",
  fontFamily: "Poppins",
  whiteSpace: "nowrap" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export type GroupType = {
  id: string;
  title: string;
  deleted: boolean;
  archived: boolean;
  color: string;
};

const HEADER_PADDING = { padding: "15px 8px 12px 8px" };
const STICKY_HEADER_CLASS =
  "bg-[#FFFFFF] sticky top-0 z-[1] border-b border-[#C3C6D4] overflow-x-hidden";

const GroupsList = ({
  board,
  transferOptions,
  options = {},
  searchValue,
  onSearchChange,
  onGroupSelect,
  onGroupPick,
}: GroupsListPropsType) => {
  const { boardName, boardId, boardGroups, boardSynced } = board;
  const { processUpdates, processSubitems } = transferOptions;
  const { batchSize } = options;
  const [internalSearch, setInternalSearch] = useState("");
  const search = searchValue ?? internalSearch;
  const showSearchInHeader = onSearchChange === undefined;
  const { isLoading: itemsLoader, isDisabled: itemsDisabled, isDisableAll } = useList();
  const addItemToGroup = useAddItemToGroup({
    boardId,
    boardName,
    processSubitems,
    processUpdates,
    batchSize,
    onGroupSelect,
  });

  const handleGroupClick = useCallback(
    (group: GroupType) => {
      if (onGroupPick) onGroupPick(group);
      else addItemToGroup(group.id);
    },
    [onGroupPick, addItemToGroup],
  );

  const groups = boardGroups[boardId];
  const filteredGroups = useSearchList<GroupType>(groups, search, "title");
  const isBoardDisabled =
    boardSynced ||
    isDisableAll ||
    itemsDisabled.some((item: string) => item.endsWith(`-${boardId}`));
  const isItemLoading = (groupId: string) => itemsLoader.includes(`${groupId}-${boardId}`);

  if (!groups) {
    return (
      <List component="div" disablePadding>
        <Spinner />
      </List>
    );
  }

  if (groups.length === 0) {
    return (
      <List component="div" disablePadding>
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-[#676879]">
          <span className="text-sm">This board has no groups.</span>
        </div>
      </List>
    );
  }

  return (
    <>
      {showSearchInHeader && (
        <div style={HEADER_PADDING} className={STICKY_HEADER_CLASS}>
          <Search
            placeholder="Search Groups"
            onChange={setInternalSearch}
            size="small"
            value={internalSearch}
          />
        </div>
      )}
      <div id="add_item_step4">
        <List component="div" disablePadding>
          {filteredGroups.map((group: GroupType) => (
            <ListItemButton
              key={group.id}
              onClick={(e) => {
                e.stopPropagation();
                handleGroupClick(group);
              }}
              sx={{ borderRadius: "4px" }}
              disabled={isBoardDisabled}
            >
              <ListItemIcon sx={{ minWidth: "unset", color: group.color }} className="!w-auto mr-2">
                <div
                  className={cn("w-4 h-4 rounded-sm")}
                  style={{ backgroundColor: group.color }}
                ></div>
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ ...LIST_ITEM_TYPOGRAPHY, color: group.color }}
                primary={group.title}
              />
              {isItemLoading(group.id) && <Spinner size={14} />}
            </ListItemButton>
          ))}
        </List>
      </div>
      <AddItemSubtitle
        subtitle={
          onGroupPick
            ? "Select a group to see which columns will sync"
            : "Select a group to add an item"
        }
      />
    </>
  );
};

export default GroupsList;
